//// This file contains 'errors' that we can safely store in the redux store (regular errors are classes and should NOT be stored in redux store)

import { FormValidationErrorType } from "./form-errors";
import { generateNewId } from "./model-customization-helpers";
import { CustomizationObjectType } from "./model-customization-types";

export type LoginError = {
    arePopupsBlocked: boolean;
    errorMessage: string;
}

export const createLoginError = (arePopupsBlocked: boolean, errorMessage: string): LoginError => {
    return { arePopupsBlocked, errorMessage };
}

/** A model customization validation error from backend with information to identify it in UI. */
export type CustomizationValidationError = {
    /** The Redux store object ID for the object that this error is about (e.g. roiId for CustomizationRois) */
    id: string;

    /** Type of the errored object. */
    type: CustomizationObjectType;

    /** A short message about the error. */
    message: string;

    /** A longer, more detailed message about the error. */
    detail: string;

    /** The field of the object that this error is about. */
    field?: string;

    /** More optional context for the error. */
    ctx?: string;
}

/** A validation error or warning for a model customization object that's directly from the UI and does not block saving (the backend still might). 
 * Technically multiple object types are supported but currently only CustomizationObjectType.Roi support has been implemented.
*/
export type FormValidationError = {
    /** The Redux store object ID for the object that this error is about (e.g. roiId for CustomizationRois) */
    id: string;

    /** The Redux store object ID for the object that this error is about (e.g. roiId for CustomizationRois). A business item may have
     * multiple warnings or errors -- they are represented as individual FormValidationError objects.
     */
    itemId: string;

    /** Type of the errored object. */
    itemType: CustomizationObjectType;

    /** Type of this error, such as duplicate names in same customization.*/
    errorType: FormValidationErrorType;

    /** A short message about the error. */
    message?: string;

    /** A longer, more detailed message about the error. */
    detail?: string;

    /** The field of the object that this error is about. */
    field?: string;

    /** More optional context for the error. */
    ctx?: string;
}

/** A daemon validation error from backend with information to identify it in UI. */
export type DaemonValidationError = {
    /** The ID of this error, usually matching the daemon configuration item this errors is for. */
    id: string;

    /** A short message about the error. */
    message: string;

    /** A longer, more detailed message about the error. */
    detail: string;

    /** The field of the object that this error is about. */
    field?: string;

    /** More optional context for the error. */
    ctx?: string;
}

/** Generates a new form validation error instance according to input. Undefined fields are not included in the object at all. */
export const generateNewFormValidationError = (errorType: FormValidationErrorType,
    itemId: string,
    itemType: CustomizationObjectType,
    message?: string,
    detail?: string,
    field?: string,
    ctx?: string): FormValidationError => {
    const newError: FormValidationError = {
        id: generateNewId(),
        itemId: itemId,
        itemType: itemType,
        errorType: errorType,
    }

    if (message) { newError.message = message; }
    if (detail) { newError.detail = detail; }
    if (field) { newError.field = field; }
    if (ctx) { newError.ctx = ctx; }

    return newError;
}

/** Duplicates given form validation error object with a new id. */
export const duplicateFormValidationError = (input: FormValidationError, duplicateTargetId: string): FormValidationError => {
    return {
        id: generateNewId(),
        itemId: duplicateTargetId,
        itemType: input.itemType,
        errorType: input.errorType,
        message: input.message,
        detail: input.detail,
        field: input.field,
        ctx: input.ctx,
    }
}
